import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import './InfoTooltip.css';

interface InfoTooltipProps {
  title?: string;
  content: React.ReactNode;
  /**
   * When true, the tooltip also opens on cursor hover — but only on devices that actually have a
   * hovering pointer (i.e. desktop). Touch devices keep the tap-to-toggle behaviour unchanged.
   */
  openOnHover?: boolean;
  /** Render the dark (ink background, white text) tooltip box on every viewport. */
  dark?: boolean;
}

const InfoTooltip: React.FC<InfoTooltipProps> = ({
  title,
  content,
  openOnHover = false,
  dark = false,
}) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ bottom: number; left: number } | null>(null);
  const wrapRef = useRef<HTMLSpanElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [hoverEnabled] = useState(
    () =>
      openOnHover &&
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches,
  );

  // The pointer type of the most recent interaction. Hover-open is for a real mouse only; a
  // touch or pen tap must always fall through to tap-to-toggle, even on a hybrid device whose
  // media query reports `hover: hover` (e.g. a touchscreen laptop).
  const lastPointerType = useRef<string>('mouse');

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  // A short close delay lets the cursor cross the small gap between the icon and the portalled
  // box (which is a sibling on <body>, not a DOM descendant) without the tooltip flickering shut.
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };

  useEffect(() => cancelClose, []);

  const isHoverMouse = (pointerType: string) => hoverEnabled && pointerType === 'mouse';

  const handlePointerEnter = (e: React.PointerEvent) => {
    if (isHoverMouse(e.pointerType)) {
      cancelClose();
      setOpen(true);
    }
  };
  const handlePointerLeave = (e: React.PointerEvent) => {
    if (isHoverMouse(e.pointerType)) scheduleClose();
  };
  const handleClick = () => {
    // A mouse on a hover device already drives visibility via enter/leave — a click there must
    // not toggle it shut. Touch / pen (and every non-hover context) keep tap-to-toggle.
    if (isHoverMouse(lastPointerType.current)) {
      setOpen(true);
    } else {
      setOpen((v) => !v);
    }
  };

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        wrapRef.current && !wrapRef.current.contains(target) &&
        boxRef.current && !boxRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    // Rendered through a portal at viewport-fixed coordinates rather than as a plain
    // position:absolute child — a nested absolute box gets clipped whenever an ancestor sets
    // overflow:hidden (e.g. the pricing comparison table's rounded-corner wrapper), which cut
    // the box off entirely when it opened upward near the top of that container. Anchoring by
    // `bottom` (distance from the viewport's bottom edge) rather than `top` still lets the box
    // grow upward from the trigger without needing to know its height in advance.
    const reposition = () => {
      if (!wrapRef.current) return;
      const rect = wrapRef.current.getBoundingClientRect();
      const boxWidth = Math.min(300, window.innerWidth - 24);
      const left = Math.min(Math.max(rect.left, 12), window.innerWidth - boxWidth - 12);
      setCoords({ bottom: window.innerHeight - rect.top + 10, left });
    };
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open]);

  return (
    <span
      className="info-tooltip-wrap"
      ref={wrapRef}
      onPointerDown={(e) => {
        lastPointerType.current = e.pointerType || 'mouse';
      }}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
    >
      <button
        type="button"
        className="info-tooltip-btn"
        onClick={handleClick}
        aria-label="More information"
      >
        i
      </button>
      {open && coords && ReactDOM.createPortal(
        <div
          className={`info-tooltip-box${dark ? ' info-tooltip-box--dark' : ''}`}
          ref={boxRef}
          style={{ bottom: coords.bottom, left: coords.left }}
          onMouseEnter={hoverEnabled ? cancelClose : undefined}
          onMouseLeave={hoverEnabled ? scheduleClose : undefined}
        >
          {title && <div className="info-tooltip-title">{title}</div>}
          <div className="info-tooltip-body">{content}</div>
        </div>,
        document.body,
      )}
    </span>
  );
};

export default InfoTooltip;
